import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { ClassEnrollmentsApi } from "@/openapi/generated";
import { recentEnrollmentStatusChangesQueryKey } from "@/features/enrollments/hooks/useRecentEnrollmentStatusChanges";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import {
  dropEnrollmentById,
  rejectEnrollmentById,
  cancelScheduledDropById,
  scheduleEnrollmentDrop,
} from "@/lib/services/classEnrollmentActions";
import {
  patchEnrollmentAfterScheduledDropCancel,
  syncEnrollmentCachesAfterAdminScheduleDrop,
  syncEnrollmentCachesAfterImmediateDrop,
} from "@/lib/utils/enrollmentQueryCache";
import { invalidatePostedTuitionLedgerQueries } from "@/features/tuition/hooks/invalidate-posted-tuition-ledger-queries";
import { runEnrollmentReviewAction } from "@/lib/enrollments/enrollment-review-actions";
import {
  StaffReviewConflictError,
  formatStaffReviewConflictMessage,
} from "@/lib/review-concurrency/staff-review-conflict";

export type DropEnrollmentIntent =
  | "immediate"
  | "scheduleFuture"
  | "approveScheduled";

async function invalidateEnrollmentQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  await queryClient.invalidateQueries({
    queryKey: [QueryKeys.ClassesEnrollmentsByStudentId],
  });
  await queryClient.invalidateQueries({
    queryKey: recentEnrollmentStatusChangesQueryKey,
  });
  await queryClient.invalidateQueries({
    queryKey: [QueryKeys.Students],
  });
  await queryClient.invalidateQueries({
    queryKey: [QueryKeys.StudentAvailableClasses],
  });
  await queryClient.invalidateQueries({
    queryKey: [QueryKeys.WidgetData],
  });
  await queryClient.invalidateQueries({
    predicate: (query) => {
      return (
        Array.isArray(query.queryKey) &&
        query.queryKey.some(
          (key) =>
            typeof key === "string" &&
            (key.includes("enrollment") ||
              key.includes("Enrollment") ||
              key.includes("Available")),
        )
      );
    },
  });
  await invalidatePostedTuitionLedgerQueries(queryClient);
}

function showEnrollmentMutationError(
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
  error: unknown,
  fallback: string,
  action: "approve" | "reject" | "cancel" | "generic" = "generic",
  subject: "request" | "withdrawal" = "request",
) {
  if (error instanceof StaffReviewConflictError) {
    toast({
      title: formatStaffReviewConflictMessage(error.conflict, action, subject),
      status: "info",
      duration: 5000,
      isClosable: true,
      position: "top-right",
    });
    return;
  }

  const errorMessage = error instanceof Error ? error.message : fallback;
  toast({
    title: "Error",
    description: errorMessage,
    status: "error",
    duration: 5000,
    isClosable: true,
    position: "top-right",
  });
}

export const useEnrollmentStatusUpdate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate: approveEnrollment, isPending: isApproving } = useMutation({
    mutationFn: async ({ enrollmentId }: { enrollmentId: number }) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      await runEnrollmentReviewAction(async () => {
        const service = createAuthenticatedApi(ClassEnrollmentsApi, accessToken);
        await service.apiClassEnrollmentsIdApprovePut({ id: enrollmentId });
      });
    },
    onSuccess: async () => {
      await invalidateEnrollmentQueries(queryClient);

      toast({
        title: "Success",
        description: "Enrollment approved successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: unknown) => {
      showEnrollmentMutationError(
        toast,
        error,
        "Failed to approve enrollment",
        "approve",
      );
    },
  });

  const { mutate: rejectEnrollment, isPending: isRejecting } = useMutation({
    mutationFn: async ({ enrollmentId }: { enrollmentId: number }) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      await runEnrollmentReviewAction(async () => {
        await rejectEnrollmentById(accessToken, enrollmentId);
      });
    },
    onSuccess: async () => {
      await invalidateEnrollmentQueries(queryClient);

      toast({
        title: "Success",
        description: "Enrollment rejected successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: unknown) => {
      showEnrollmentMutationError(
        toast,
        error,
        "Failed to reject enrollment",
        "reject",
      );
    },
  });

  const { mutate: cancelScheduledDrop, isPending: isCancelingScheduledDrop } =
    useMutation({
      mutationFn: async ({
        enrollmentId,
      }: {
        enrollmentId: number;
        studentId?: number;
      }) => {
        if (!accessToken) {
          throw new Error("Authentication token is required");
        }
        await runEnrollmentReviewAction(async () => {
          await cancelScheduledDropById(accessToken, enrollmentId);
        });
      },
      onSuccess: async (_data, { enrollmentId, studentId }) => {
        if (studentId != null) {
          patchEnrollmentAfterScheduledDropCancel(
            queryClient,
            studentId,
            enrollmentId,
          );
        }
        await invalidateEnrollmentQueries(queryClient);

        toast({
          title: "Withdrawal canceled",
          description: "The scheduled withdrawal has been canceled. The student remains enrolled in the class.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      },
      onError: (error: unknown) => {
        showEnrollmentMutationError(
          toast,
          error,
          "Failed to cancel withdrawal request",
          "cancel",
          "withdrawal",
        );
      },
    });

  const { mutate: scheduleEnrollmentDropMutation, isPending: isSchedulingDrop } =
    useMutation({
      mutationFn: async ({ enrollmentId }: { enrollmentId: number }) => {
        if (!accessToken) {
          throw new Error("Authentication token is required");
        }
        return scheduleEnrollmentDrop(accessToken, enrollmentId);
      },
      onSuccess: async () => {
        await invalidateEnrollmentQueries(queryClient);

        toast({
          title: "Request submitted",
          description:
            "Your withdrawal request has been sent for studio approval.",
          status: "success",
          duration: 4000,
          isClosable: true,
          position: "top-right",
        });
      },
      onError: (error: unknown) => {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to submit withdrawal request";

        toast({
          title: "Error",
          description: errorMessage,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      },
    });

  const { mutate: dropEnrollment, isPending: isDropping } = useMutation({
    mutationFn: async ({
      enrollmentId,
      dropDateYmd,
      intent,
    }: {
      enrollmentId: number;
      dropDateYmd: string;
      intent?: DropEnrollmentIntent;
      className?: string;
      studentId?: number;
      studentName?: string;
      classId?: number | null;
    }) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      await runEnrollmentReviewAction(async () => {
        await dropEnrollmentById(
          accessToken,
          enrollmentId,
          dropDateYmd,
          intent === "approveScheduled",
        );
      });
    },
    onSuccess: async (_data, variables) => {
      const isScheduledWithdraw =
        variables.intent === "scheduleFuture" ||
        variables.intent === "approveScheduled";
      const isImmediateWithdraw = variables.intent === "immediate";

      const dropCacheParams =
        isScheduledWithdraw || isImmediateWithdraw
          ? {
              enrollmentId: variables.enrollmentId,
              dropDateYmd: variables.dropDateYmd,
              studentId: variables.studentId,
              studentName: variables.studentName,
              className: variables.className,
              classId: variables.classId,
            }
          : null;

      if (dropCacheParams) {
        if (isScheduledWithdraw) {
          syncEnrollmentCachesAfterAdminScheduleDrop(
            queryClient,
            dropCacheParams,
          );
        } else {
          syncEnrollmentCachesAfterImmediateDrop(
            queryClient,
            dropCacheParams,
          );
        }
      }

      await invalidateEnrollmentQueries(queryClient);

      if (dropCacheParams) {
        if (isScheduledWithdraw) {
          syncEnrollmentCachesAfterAdminScheduleDrop(
            queryClient,
            dropCacheParams,
          );
        } else {
          syncEnrollmentCachesAfterImmediateDrop(
            queryClient,
            dropCacheParams,
          );
        }
      }

      const classLabel = variables.className
        ? `"${variables.className}"`
        : "the class";

      const description =
        variables.intent === "approveScheduled"
          ? `Withdrawal approved. The student will be withdrawn from ${classLabel} on the selected date.`
          : variables.intent === "immediate"
            ? `Successfully dropped from ${classLabel}.`
            : `Withdrawal from ${classLabel} scheduled successfully.`;

      toast({
        title: "Success",
        description,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error, variables) => {
      showEnrollmentMutationError(
        toast,
        error,
        "Failed to withdraw student from class",
        "approve",
        variables.intent === "approveScheduled" ? "withdrawal" : "request",
      );
    },
  });

  return {
    approveEnrollment,
    rejectEnrollment,
    cancelScheduledDrop,
    scheduleEnrollmentDrop: scheduleEnrollmentDropMutation,
    dropEnrollment,
    isApproving,
    isRejecting,
    isCancelingScheduledDrop,
    isSchedulingDrop,
    isDropping,
  };
};
